import React, { useCallback, useEffect, useState } from "react";
import {
    IActiveProspectState,
    ICompanyInfoState,
    IHatchState,
    ILeadPerfectionDataState,
    IMarketSharpState,
    IMarlimarState,
    IZapierState,
    Ii360State
} from "./IState";
import { route, getAxios } from "../../utils/endpoints";
import { User } from "../../model/User";
import { IProfileInfo } from "../../types/ProfileInfo";
import { isPlainObject } from "@reduxjs/toolkit";
import { objectToArrayOfObjects } from "../../utils/object-to-object-array";
import { ConsoleLogger } from "@microsoft/signalr/dist/esm/Utils";

export const useEditProfileForm = (username?: string) => {
    const user = new User();
    const selectedUsername: string = username === undefined ? user.UserName : username;

    const [secLevel, setSecLevel] = useState<number>(0);

    const [snackbarLevel, setSnackbarLevel] = useState<"success" | "error">("error");
    const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = useState<string>("");

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [pauseLoading, setPauseLoading] = useState<boolean>(false);

    const [activeProspect, setActiveProspect] = useState<IActiveProspectState>({
        activeProspectWebhookAddress: ""
    });

    const [companyInfo, setCompanyInfo] = useState<ICompanyInfoState>({
        companyName: "",
        contactPhone: "",
        internetLeadCCEmail: "",
        internetLeadEmail: "",
        phoneLeadCCEmail: "",
        phoneLeadEmailAddress: "",
        primaryContact: "",
        sendEmail: false,
        isLeadPaused: false
    });

    const [hatch, setHatch] = useState<IHatchState>({
        clientId: "",
        clientToken: "",
        webhookAddress: ""
    });

    const [leadPerfection, setLeadPerfection] = useState<ILeadPerfectionDataState>({
        lpProduct: "",
        lpSource: "",
        lpSubsourceID: 0,
        lpurl: ""
    });

    const [marketSharp, setMarketSharp] = useState<IMarketSharpState>({
        captureName: "",
        coy: "",
        defaultSource: "",
        formKey: "",
        sourceKey: ""
    });

    const [marlimar, setMarlimar] = useState<IMarlimarState>({
        hashKey: "",
        webhookAddress: ""
    });

    const [zapier, setZapier] = useState<IZapierState>({
        zapierWebhookAddress: ""
    });

    const [i360, setI360] = useState<Ii360State>({
        defaultInterest: "",
        defaultSource: "",
        defaultSourceType: "",
        webhookAddress: ""
    });

    const [sectionFields, setSectionFields] = useState<{
        leadPerfection: object[];
        marketSharp: object[];
        i360: object[];
        hatch: object[];
        activeProspect: object[];
        zapier: object[];
        marlimar: object[];
    }>({
        leadPerfection: [],
        marketSharp: [],
        i360: [],
        hatch: [],
        activeProspect: [],
        zapier: [],
        marlimar: []
    });

    const onAddField = (objName: string, name: string, value: string) => {
        setSectionFields((prevState) => ({
            ...prevState,
            [objName]: [...prevState[objName as keyof typeof sectionFields], { [name]: value }]
        }));
    }

    const onPauseLeads = async (): Promise<void> => {
        try {
            await getAxios(`/profile/edit/${selectedUsername}/pause`, "PUT", {
                paused: !companyInfo.isLeadPaused
            });
    
        } catch (error) {
            console.error(error);            
        } finally {
            onFetchPauseLeads();
        }
    }

    const onFetchPauseLeads = async () => {
        setPauseLoading(true);

        try {
            const response = await getAxios(`/profile/edit/${selectedUsername}/pause`, "GET");

            const data: { paused: boolean } = await response.data;

            setCompanyInfo((prevState) => ({
                ...prevState,
                isLeadPaused: data.paused
            }));
        } catch (error) {
            console.error(error);
        } finally {
            setPauseLoading(false);
        }
    }

    const onChangeField = (event: React.ChangeEvent<HTMLInputElement>, objName: string, index: number) => {
        setSectionFields((prevState) => {
            return {
                ...prevState,
                [objName]: prevState[objName as keyof typeof prevState]
                    .map((obj, i) => {
                        if (i === index) {
                            return { ...isPlainObject, [event.target.name]: event.target.value }
                        }

                        return obj;
                    })
            }
        })
    }

    const fetchUserData = async (): Promise<void> => {
        setIsLoading(true);

        try {
            const response = await getAxios(`${route.apiUsers.edit}/${selectedUsername}`, "GET")

            const data: IProfileInfo = await response.data;

            setSecLevel(parseInt(data.userSecLevel!));

            setCompanyInfo((prevState) => ({
                ...prevState,
                companyName: data.companyName,
                contactPhone: data.contactPhone,
                internetLeadCCEmail: data.internetLeadCCEmail,
                internetLeadEmail: data.internetLeadEmail,
                phoneLeadCCEmail: data.phoneLeadCCEmail,
                phoneLeadEmailAddress: data.phoneLeadEmailAddress,
                primaryContact: data.primaryContact,
                sendEmail: data.sendEmail,
            }))

            setMarketSharp({
                captureName: data.mscCaptureName,
                coy: data.mscCoy,
                defaultSource: data.mscSource,
                formKey: data.mscFormKey,
                sourceKey: data.mscSourceKey
            });

            setLeadPerfection({
                lpProduct: data.lpProduct,
                lpSource: data.lpSource,
                lpSubsourceID: data.lpSubsourceID,
                lpurl: data.lpurl
            });

            setHatch({
                clientId: data.htcClientId,
                clientToken: data.htcClientToken,
                webhookAddress: data.htcApiAddress
            });

            setI360({
                defaultInterest: data.ipcInterest,
                defaultSource: data.ipcSource,
                defaultSourceType: data.ipcSourceType,
                webhookAddress: data.ipcApiAddress
            });

            setZapier({
                zapierWebhookAddress: data.zpcApiAddress
            });

            setMarlimar({
                hashKey: data.mmcHashKey,
                webhookAddress: data.mmcApiAddress
            });

            setActiveProspect({
                activeProspectWebhookAddress: data.apcApiAddress
            });

            const fields = JSON.parse(data.jsonCustomFields as string);

            if (fields) {
                const leadPerfection = fields.LeadPerfection ? objectToArrayOfObjects(fields.LeadPerfection) : [];
                const marketSharp = fields.MarketSharp ? objectToArrayOfObjects(fields.MarketSharp) : [];
                const i360 = fields.i360 ? objectToArrayOfObjects(fields.i360) : [];
                const hatch = fields.Hatch ? objectToArrayOfObjects(fields.Hatch) : [];
                const activeProspect = fields.ActiveProspect ? objectToArrayOfObjects(fields.ActiveProspect) : [];
                const zapier = fields.Zapier ? objectToArrayOfObjects(fields.Zapier) : [];
                const marlimar = fields.Marlimar ? objectToArrayOfObjects(fields.Marlimar) : [];

                console.log("xapier: ", zapier);

                setSectionFields((prevState) => ({
                    ...prevState,
                    leadPerfection: leadPerfection,
                    activeProspect: activeProspect,
                    marketSharp: marketSharp,
                    i360: i360,
                    zapier: zapier,
                    marlimar: marlimar,
                    hatch: hatch
                }));
            }

        } catch (error) {
            setSnackbarLevel("error");
            setSnackbarMessage("Could not get user information");
            setSnackbarOpen(true);
        } finally {
            setIsLoading(false);
        }
    }

    const formatToArr = (obj?: any) => {
        if (!obj) alert("No data");

        return Object.keys(obj).map((key) => ({ name: key, value: obj[key] }));
    }

    const onUpdateUserProfile = async (): Promise<void> => {
        setIsLoading(true);

        const responseBody: IProfileInfo = {
            companyName: companyInfo.companyName,
            contactPhone: companyInfo.contactPhone,
            htcApiAddress: hatch.webhookAddress,
            htcClientId: hatch.clientId,
            htcClientToken: hatch.clientToken,
            internetLeadCCEmail: companyInfo.internetLeadCCEmail,
            internetLeadEmail: companyInfo.internetLeadEmail,
            ipcApiAddress: i360.webhookAddress,
            ipcInterest: i360.defaultInterest,
            ipcSource: i360.defaultSource,
            ipcSourceType: i360.defaultSourceType,
            lpProduct: leadPerfection.lpProduct,
            lpSource: leadPerfection.lpSource,
            lpSubsourceID: leadPerfection.lpSubsourceID,
            lpurl: leadPerfection.lpurl,
            mscCaptureName: marketSharp.captureName,
            mscCoy: marketSharp.coy,
            mscFormKey: marketSharp.formKey,
            mscSource: marketSharp.defaultSource,
            mscSourceKey: marketSharp.sourceKey,
            phoneLeadCCEmail: companyInfo.phoneLeadCCEmail,
            phoneLeadEmailAddress: companyInfo.phoneLeadEmailAddress,
            primaryContact: companyInfo.primaryContact,
            sendEmail: companyInfo.sendEmail,
            apcApiAddress: activeProspect.activeProspectWebhookAddress,
            zpcApiAddress: zapier.zapierWebhookAddress,
            mmcApiAddress: marlimar.webhookAddress,
            mmcHashKey: marlimar.hashKey,
            jsonCustomFields: {
                LeadPerfection: toObject(sectionFields.leadPerfection),
                ActiveProspect: toObject(sectionFields.activeProspect),
                MarketSharp: toObject(sectionFields.marketSharp),
                Hatch: toObject(sectionFields.hatch),
                i360: toObject(sectionFields.i360),
                Marlimar: toObject(sectionFields.marlimar),
                Zapier: toObject(sectionFields.zapier)
            }
        }

        try {
            const response = await getAxios(`${route.apiUsers.edit}/${selectedUsername}`, "PUT", {
                ...responseBody,
                jsonCustomFields: JSON.stringify(responseBody.jsonCustomFields)
            });

            console.info("[INFO] ", await response.data);

            setSnackbarLevel("success");
            setSnackbarMessage("Your profile has been updated");
            setSnackbarOpen(true);
        } catch (error: unknown) {
            setSnackbarLevel("error");
            setSnackbarMessage((error as any).data);
            setSnackbarOpen(true);
        } finally {
            setIsLoading(false);
        }
    }

    const onCloseSnackbar = () => {
        setSnackbarOpen(false);
    }

    const toObject = (fields: Array<any>): object => {
        const object = {};

        fields.map((obj, index) => {
            Object.assign(object, obj);
        });

        return object;
    }

    useEffect(() => {
        fetchUserData();
    }, [])

    useEffect(() => {
        onFetchPauseLeads();
    }, [])

    return {
        activeProspect,
        companyInfo,
        hatch,
        leadPerfection,
        marketSharp,
        marlimar,
        zapier,
        secLevel,
        i360,
        sectionFields,
        isLoading,
        snackbarLevel,
        snackbarOpen,
        snackbarMessage,
        setSnackbarLevel,
        setSnackbarOpen,
        setSnackbarMessage,
        setActiveProspect,
        setCompanyInfo,
        setHatch,
        setLeadPerfection,
        setMarketSharp,
        setMarlimar,
        setZapier,
        setI360,
        onCloseSnackbar,
        onUpdateUserProfile,
        onChangeField,
        onAddField,
        onPauseLeads,
        toObject,
        pauseLoading,
    }
}

/** 
 * 
 * 
    const [isNewInputOpen, setIsNewInputOpen] = useState<boolean>(false);
    const [snackbarLevel, setSnackbarLevel] = useState<"sucess" | "error">("error");
    const [snackbarOpen, setSnackbarOpen] = useState<boolean>(false);
    const [snackbarMessage, setSnackbarMessage] = useState<string>("");

    const [state, setState] = useState<IState>({
        companyInfo: {
            companyName: "",
            contactPhone: "",
            internetLeadCCEmail: "",
            internetLeadEmail: "", 
            phoneLeadCCEmail: "",
            phoneLeadEmailAddress: "",
            primaryContact: "",
            sendEmail: false,
        },
        leadPerfectionData: {
            lpProduct: "",
            lpSource: "",
            lpSubsourceID: 0,
            lpurl: ""
        },
        marketSharp: {
            captureName: "",
            coy: "",
            defaultSource: "",
            formKey: "",
            sourceKey: ""
        },
        i360: {
            defaultInterest: "",
            defaultSource: "",
            defaultSourceType: "",
            webhookAddress: "",
        },
        hatch: {
            clientId: "",
            clientToken: "",
            webhookAddress: ""
        },
        activeProspect: {
            activeProspectWebhookAddress: ""
        },
        zapier: {
            zapierWebhookAddress: ""
        },
        marlimar: {
            webhookAddress: "",
            hashKey: ""
        }
    });

    const onClose = () => {
        setSnackbarOpen(false);
    }

    const fetchUserData = async (): Promise<void> => {
        try {
            const response = await getAxios(`${route.apiUsers.edit}/${selectedUsername}`, "GET")
            
            const data: IProfileInfo = await response.data;

            setState((prevState) => ({
                ...prevState,
                companyInfo: {
                    companyName: data.companyName,
                    contactPhone: data.contactPhone,
                    internetLeadCCEmail: data.internetLeadCCEmail,
                    internetLeadEmail: data.internetLeadEmail,
                    phoneLeadCCEmail: data.phoneLeadCCEmail,
                    phoneLeadEmailAddress: data.phoneLeadEmailAddress,
                    primaryContact: data.primaryContact,
                    sendEmail: data.sendEmail,
                },
                marketSharp: {
                    captureName: data.mscCaptureName,
                    coy: data.mscCoy,
                    defaultSource: data.mscSource,
                    formKey: data.mscFormKey,
                    sourceKey: data.mscSourceKey
                },
                leadPerfectionData: {
                    lpProduct: data.lpProduct,
                    lpSource: data.lpSource,
                    lpSubsourceID: data.lpSubsourceID,
                    lpurl: data.lpurl
                },
                hatch: {
                    clientId: data.htcClientId,
                    clientToken: data.htcClientToken,
                    webhookAddress: data.htcApiAddress
                },
                i360: {
                    defaultInterest: data.ipcInterest,
                    defaultSource: data.ipcSource,
                    defaultSourceType: data.ipcSourceType,
                    webhookAddress: data.ipcApiAddress
                },
                zapier: {
                    zapierWebhookAddress: data.zpcApiAddress
                },
                marlimar: {
                    hashKey: data.mmcHashKey,
                    webhookAddress: data.mmcApiAddress
                }
            }))
        }

        catch (error) {
            console.log(error);
        }
    }

    const onUpdate = async (): Promise<void> => {
        const responseBody: IProfileInfo = {
            companyName: state.companyInfo.companyName,
            contactPhone: state.companyInfo.contactPhone,
            htcApiAddress: state.hatch.webhookAddress,
            htcClientId: state.hatch.clientId,
            htcClientToken: state.hatch.clientToken,
            internetLeadCCEmail: state.companyInfo.internetLeadCCEmail,
            internetLeadEmail: state.companyInfo.internetLeadEmail,
            ipcApiAddress: state.i360.webhookAddress,
            ipcInterest: state.i360.defaultInterest,
            ipcSource: state.i360.defaultSource,
            ipcSourceType: state.i360.defaultSourceType,
            lpProduct: state.leadPerfectionData.lpProduct,
            lpSource: state.leadPerfectionData.lpSource,
            lpSubsourceID: state.leadPerfectionData.lpSubsourceID,
            lpurl: state.leadPerfectionData.lpurl,
            mscCaptureName: state.marketSharp.captureName,
            mscCoy: state.marketSharp.coy,
            mscFormKey: state.marketSharp.formKey,
            mscSource: state.marketSharp.defaultSource,
            mscSourceKey: state.marketSharp.sourceKey,
            phoneLeadCCEmail: state.companyInfo.phoneLeadCCEmail,
            phoneLeadEmailAddress: state.companyInfo.phoneLeadEmailAddress,
            primaryContact: state.companyInfo.primaryContact,
            sendEmail: state.companyInfo.sendEmail,
            apcApiAddress: state.activeProspect.activeProspectWebhookAddress,
            zpcApiAddress: state.zapier.zapierWebhookAddress,
            mmcApiAddress: state.marlimar.webhookAddress,
            mmcHashKey: state.marlimar.hashKey   
        }
        
        try {
            const response = await getAxios(`${route.apiUsers.edit}/${selectedUsername}`, "PUT", responseBody);

            setSnackbarLevel("sucess");
            setSnackbarMessage("Your profile has been upodated");
            setSnackbarOpen(true);

        } catch (error) {

            setSnackbarLevel("error");
            setSnackbarMessage((error as any).data);
            setSnackbarOpen(true);
        }
    }

    const onChange = (event: React.ChangeEvent<HTMLInputElement>, sectionName: string): void => {
        const { name, value } = event.target;

        setState((prevState) => ({
            ...prevState,
            [sectionName]: {
                ...prevState[sectionName as keyof typeof state],
                [name]: value
            }
        }));
    }

    const onChangeCheckBox = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const { checked } = event.target;

        setState((prevState) => ({
            ...prevState,
            companyInfo: {
                ...prevState.companyInfo,
                sendEmail: checked
            }
        }));
    }

    useEffect(() => {
        fetchUserData();
    }, []);

    return {
        state,
        isNewInputOpen,
        setIsNewInputOpen,
        snackbarOpen,
        snackbarLevel,
        snackbarMessage,
        onClose, 
        onChange, 
        onChangeCheckBox, 
        onUpdate, 
        fetchUserData
    };
*/
